import React, { useState, useEffect } from 'react';
import { Calendar, Clock, AlertTriangle, Plus, Edit, Trash2, X, Save } from 'lucide-react';
import { scheduleExceptionService } from '../firebase/scheduleExceptionService';

const ScheduleExceptions = () => {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingException, setEditingException] = useState(null);
  
  const [formData, setFormData] = useState({
    date: '',
    reason: '',
    closureType: 'full', // 'full', 'lunch', 'dinner', 'custom'
    customShifts: [{ start: '', end: '' }],
    message: ''
  });

  // Horarios predefinidos
  const defaultSchedule = {
    lunch: { start: '13:00', end: '16:00' },
    dinner: { start: '20:00', end: '22:30' }
  };

  useEffect(() => {
    fetchExceptions();
  }, []);

  const fetchExceptions = async () => {
    try {
      setLoading(true);
      const data = await scheduleExceptionService.getAllExceptions();
      setExceptions(data);
    } catch (err) {
      setError('Error al cargar las excepciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      reason: '',
      closureType: 'full',
      customShifts: [{ start: '', end: '' }],
      message: ''
    });
    setEditingException(null);
  };

  const openModal = (exception = null) => {
    if (exception) {
      setEditingException(exception);
      setFormData({
        date: exception.date,
        reason: exception.reason,
        closureType: exception.closureType || 'custom',
        customShifts: exception.shifts.length > 0 ? exception.shifts : [{ start: '', end: '' }],
        message: exception.message || exception.reason
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convertir fecha al formato dd/mm/yyyy
      const [year, month, day] = formData.date.split('-');
      const formattedDate = `${day}/${month}/${year}`;
      
      let shifts = [];
      
      // Determinar los turnos según el tipo de cierre
      switch (formData.closureType) {
        case 'full':
          shifts = []; // Sin turnos = completamente cerrado
          break;
        case 'lunch':
          shifts = [defaultSchedule.dinner]; // Solo cenas disponibles
          break;
        case 'dinner':
          shifts = [defaultSchedule.lunch]; // Solo comidas disponibles
          break;
        case 'custom':
          shifts = formData.customShifts.filter(shift => shift.start && shift.end);
          break;
      }

      const exceptionData = {
        date: formattedDate,
        reason: formData.reason,
        message: formData.message || formData.reason,
        shifts: shifts,
        closureType: formData.closureType,
        createdAt: new Date().toISOString()
      };

      if (editingException) {
        await scheduleExceptionService.updateException(editingException.id, exceptionData);
      } else {
        await scheduleExceptionService.createException(exceptionData);
      }

      await fetchExceptions();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError('Error al guardar la excepción');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta excepción?')) {
      return;
    }

    try {
      await scheduleExceptionService.deleteException(id);
      await fetchExceptions();
    } catch (err) {
      setError('Error al eliminar la excepción');
      console.error(err);
    }
  };

  const addCustomShift = () => {
    setFormData(prev => ({
      ...prev,
      customShifts: [...prev.customShifts, { start: '', end: '' }]
    }));
  };

  const removeCustomShift = (index) => {
    setFormData(prev => ({
      ...prev,
      customShifts: prev.customShifts.filter((_, i) => i !== index)
    }));
  };

  const updateCustomShift = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customShifts: prev.customShifts.map((shift, i) => 
        i === index ? { ...shift, [field]: value } : shift
      )
    }));
  };

  const getClosureTypeLabel = (type) => {
    switch (type) {
      case 'full': return 'Día completo';
      case 'lunch': return 'Solo comidas';
      case 'dinner': return 'Solo cenas';
      case 'custom': return 'Personalizado';
      default: return 'Desconocido';
    }
  };

  const getShiftsDisplay = (shifts, closureType) => {
    if (closureType === 'full') return 'Cerrado todo el día';
    if (shifts.length === 0) return 'Sin horarios disponibles';
    
    return shifts.map(shift => `${shift.start} - ${shift.end}`).join(', ');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right text-xl"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestión de Horarios</h1>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          <Plus size={20} />
          Nueva Excepción
        </button>
      </div>

      {/* Lista de excepciones */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Motivo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo de Cierre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Horarios Disponibles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {exceptions.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  No hay excepciones configuradas
                </td>
              </tr>
            ) : (
              exceptions.map((exception) => (
                <tr key={exception.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Calendar size={16} className="text-gray-400 mr-2" />
                      {exception.date}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {exception.reason}
                    </div>
                    {exception.message && exception.message !== exception.reason && (
                      <div className="text-sm text-gray-500">
                        Mensaje: {exception.message}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      exception.closureType === 'full' 
                        ? 'bg-red-100 text-red-800'
                        : exception.closureType === 'lunch'
                        ? 'bg-yellow-100 text-yellow-800'
                        : exception.closureType === 'dinner'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getClosureTypeLabel(exception.closureType)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock size={16} className="mr-1" />
                      {getShiftsDisplay(exception.shifts, exception.closureType)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openModal(exception)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(exception.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para crear/editar excepción */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {editingException ? 'Editar Excepción' : 'Nueva Excepción'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Cierre
                  </label>
                  <select
                    value={formData.closureType}
                    onChange={(e) => setFormData(prev => ({ ...prev, closureType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="full">Cerrar todo el día</option>
                    <option value="lunch">Cerrar solo comidas</option>
                    <option value="dinner">Cerrar solo cenas</option>
                    <option value="custom">Horario personalizado</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Ej: Evento privado, Vacaciones, Mantenimiento..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje para los clientes (opcional)
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Mensaje personalizado que verán los clientes al intentar reservar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si está vacío, se usará el motivo como mensaje
                </p>
              </div>

              {/* Horarios personalizados */}
              {formData.closureType === 'custom' && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Horarios Disponibles
                    </label>
                    <button
                      type="button"
                      onClick={addCustomShift}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                    >
                      <Plus size={16} />
                      Añadir horario
                    </button>
                  </div>
                  
                  {formData.customShifts.map((shift, index) => (
                    <div key={index} className="flex items-center gap-2 mb-2">
                      <input
                        type="time"
                        value={shift.start}
                        onChange={(e) => updateCustomShift(index, 'start', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-gray-500">hasta</span>
                      <input
                        type="time"
                        value={shift.end}
                        onChange={(e) => updateCustomShift(index, 'end', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {formData.customShifts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCustomShift(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  
                  <p className="text-xs text-gray-500 mt-1">
                    Deja vacío para cerrar completamente. Los horarios aquí serán los únicos disponibles.
                  </p>
                </div>
              )}

              {/* Preview del efecto */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <AlertTriangle size={16} className="mr-1 text-yellow-500" />
                  Vista previa del efecto:
                </h4>
                <div className="text-sm text-gray-600">
                  {formData.closureType === 'full' && (
                    <p>🔒 El restaurante estará cerrado todo el día. Los clientes verán: "{formData.message || formData.reason}"</p>
                  )}
                  {formData.closureType === 'lunch' && (
                    <p>🍽️ Solo estará disponible el turno de cena (20:00-22:30). Las comidas estarán cerradas.</p>
                  )}
                  {formData.closureType === 'dinner' && (
                    <p>☀️ Solo estará disponible el turno de comida (13:00-16:00). Las cenas estarán cerradas.</p>
                  )}
                  {formData.closureType === 'custom' && (
                    <p>⚙️ Solo estarán disponibles los horarios: {
                      formData.customShifts
                        .filter(shift => shift.start && shift.end)
                        .map(shift => `${shift.start}-${shift.end}`)
                        .join(', ') || 'Ninguno (cerrado)'
                    }</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  <Save size={16} />
                  {editingException ? 'Guardar Cambios' : 'Crear Excepción'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleExceptions;